import { MissingVariableError } from "../errors";
import type { CompiledTemplate } from "../types/template";
import { getValueAtPath } from "../utils/objectPath";

export interface InterpolationResult {
  valuesByElementId: Map<string, string>;
  hiddenElementIds: Set<string>;
}

function applyTextTransform(
  value: string,
  transform: "none" | "upper" | "lower",
): string {
  if (transform === "upper") {
    return value.toUpperCase();
  }
  if (transform === "lower") {
    return value.toLowerCase();
  }
  return value;
}

function applyValueTemplate(template: string, value: string): string {
  return template.replace(/{{\s*value\s*}}/g, value);
}

export function interpolateVariables(
  compiled: CompiledTemplate,
  vars: Record<string, unknown>,
): InterpolationResult {
  const valuesByElementId = new Map<string, string>();
  const hiddenElementIds = new Set<string>();
  const valueCache = new Map<string, unknown>();

  const readValue = (path: string): unknown => {
    if (valueCache.has(path)) {
      return valueCache.get(path);
    }
    const value = getValueAtPath(vars, path);
    valueCache.set(path, value);
    return value;
  };

  for (const container of compiled.containers) {
    for (const element of container.elements) {
      if (element.type !== "text" && element.type !== "barcode") {
        continue;
      }

      const raw = readValue(element.source);

      if (element.type === "text") {
        if (raw === undefined || raw === null) {
          if (element.presence === "optional-hide") {
            hiddenElementIds.add(element.id);
          }
          continue;
        }

        const transformed = applyTextTransform(String(raw), element.transform);
        valuesByElementId.set(
          element.id,
          applyValueTemplate(element.valueTemplate, transformed),
        );
        continue;
      }

      if (raw === undefined || raw === null) {
        continue;
      }

      valuesByElementId.set(element.id, String(raw));
    }
  }

  const missingRequired = compiled.requiredVariables.filter((path) => {
    const value = readValue(path);
    return value === undefined || value === null;
  });

  if (missingRequired.length > 0) {
    throw new MissingVariableError(missingRequired);
  }

  return {
    valuesByElementId,
    hiddenElementIds,
  };
}
